import { Injectable, Signal, WritableSignal, inject, signal } from '@angular/core';
import { Company, CompanyContact, CompanyInvitation, CompanyMscompany, CompanyUsers, CompanyDetails, CompanyDisplayedSections } from '../models/company.model';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '@env/environment';
import { catchError, finalize, Observable, of, shareReplay, take, tap } from 'rxjs';
import { GS1NotificationService } from '@gs1france/gs1-components-library';
import { TranslateService } from '@ngx-translate/core';
import { COUNTRIES } from '../../../config/countries';

@Injectable({ providedIn: 'root' })
export class CompanyService {
  __URL_API__ = `${environment.URL_API}`;

  private _http = inject(HttpClient);
  private notificationService = inject(GS1NotificationService);
  private translate = inject(TranslateService);

  // Company state (reactive)
  private _company: WritableSignal<CompanyDetails | null> = signal<CompanyDetails | null>(null);
  company: Signal<CompanyDetails | null> = this._company.asReadonly();
  private _companyLoading: WritableSignal<boolean> = signal(false);
  companyLoading: Signal<boolean> = this._companyLoading.asReadonly();
  private companyRequest$: Observable<CompanyDetails> | null = null;

  getCompteEntreprise(): Observable<Company> {
    return this._http.get<Company>(this.__URL_API__ + 'api/Entreprise/GetCompteByNum').pipe(
      catchError((error) => {
        console.error(error);
        return of(null);
      })
    );
  }

  getMSCompanyEntreprise(accountNumber: string): Observable<CompanyMscompany> {
    return this._http.get<CompanyMscompany>(this.__URL_API__ + 'api/Entreprise/GetMsCompanyEntreprise?accountNumber=' + accountNumber).pipe(
      catchError((error) => {
        console.error(error);
        return of(null);
      })
    );
  }

  getCompanyDetails(): Observable<CompanyDetails> {
    return this._http.get<CompanyDetails>(this.__URL_API__ + 'api/companies').pipe(
      take(1),
      catchError((err) => {
        console.error(err);
        return of(null);
      })
    );
  }

  /**
   * Get company details with request deduplication and caching.
   * @param forceReload - If true, bypasses cache and fetches fresh data
   * @returns Observable<CompanyDetails>
   */
  getCompany(forceReload = false): Observable<CompanyDetails> {
    // Return cached value if available and not forcing reload
    if (this._company() && !forceReload) {
      return of(this._company()!);
    }

    // Return in-flight request if already loading (deduplication)
    if (this.companyRequest$) {
      return this.companyRequest$;
    }

    // Start new request
    this._companyLoading.set(true);
    this.companyRequest$ = this.getCompanyDetails().pipe(
      tap((company) => this.setCompany(company)),
      finalize(() => {
        this._companyLoading.set(false);
        this.companyRequest$ = null;
      }),
      shareReplay(1)
    );
    return this.companyRequest$;
  }

  /**
   * Set company data and update signal
   */
  setCompany(company: CompanyDetails): void {
    if (company && company.countryCode) {
      const countryMatch = COUNTRIES.find((c: any) => c.alpha2Code.toLowerCase() === company.countryCode.toLowerCase());
      if (countryMatch) {
        company.country = countryMatch.name;
      }
    }
    this._company.set(company);
  }

  /**
   * Clear company data
   */
  clearCompany(): void {
    this._company.set(null);
    this.companyRequest$ = null;
  }

  /**
   * Get which company sections should be displayed based on company data
   */
  getCompanyDisplayedSections(): CompanyDisplayedSections | undefined {
    const company = this._company();
    if (company) {
      return {
        activateCA:
          !company.activite ||
          (!company.activite.label.includes('santé') &&
            !company.activite.label.includes('Administration') &&
            !company.activite.label.includes('bancaire')),
        activateSEPA:
          company.siren &&
          (company.countryCode?.toUpperCase() === 'FR' ||
            company.countryCode?.toUpperCase() === 'FR-TOM' ||
            company.countryCode?.toUpperCase() === 'FR-DOM'),
        activateLogo: company.typeCompte === 'GS1 Partenaires' && company.packId !== 'DD6B09B2-4124-4D6C-822B-F3102BC49835',
        activatePacks: company.typeCompte === 'GS1 Partenaires',
        hasCustomerNumber: company.customerNumber !== null && company.customerNumber?.trim()?.length > 0
      };
    }
    return undefined;
  }

  updateCompteEntreprise(compteEntrepriseForm: Company): Observable<string> {
    const formData: FormData = new FormData();
    formData.append('nom', compteEntrepriseForm.nom == undefined ? '' : compteEntrepriseForm.nom.toString());
    formData.append('adresse', compteEntrepriseForm.adresse == undefined ? '' : compteEntrepriseForm.adresse.toString());
    formData.append('adresseComp', compteEntrepriseForm.adresseComp == undefined ? '' : compteEntrepriseForm.adresseComp.toString());
    formData.append('codePostale', compteEntrepriseForm.codePostale == undefined ? '' : compteEntrepriseForm.codePostale.toString());
    formData.append('ville', compteEntrepriseForm.ville == undefined ? '' : compteEntrepriseForm.ville.toString());
    formData.append('pays', compteEntrepriseForm.pays == undefined ? '' : compteEntrepriseForm.pays.toString());
    formData.append('telephone', compteEntrepriseForm.telephone == undefined ? '' : compteEntrepriseForm.telephone.toString());
    formData.append('siren', compteEntrepriseForm.siren == undefined ? '' : compteEntrepriseForm.siren.toString());

    return this._http.put<string>(this.__URL_API__ + 'api/Entreprise/Update', formData).pipe(
      take(1),
      catchError((error) => {
        console.error(error);
        return of(null);
      })
    );
  }

  updateMscompany(company: CompanyMscompany): Observable<void> {
    return this._http.patch<void>(this.__URL_API__ + 'api/Entreprise/UpdateMsCompany', company).pipe(
      take(1),
      catchError((error) => {
        console.error(error);
        return of(null);
      })
    );
  }

  updateCompanyDetails(company: CompanyDetails, isFromActivity: boolean = true): Observable<CompanyDetails> {
    return this._http.patch<CompanyDetails>(this.__URL_API__ + 'api/companies', company, { headers: { 'Content-Type': 'application/json' } }).pipe(
      take(1),
      tap(() =>
        this.notificationService.addNotification(this.translate.instant(isFromActivity ? 'Company.Activity.updateSuccess' : 'Company.Identity.Notification.success'), 'success')
      ),
      catchError((err) => {
        this.notificationService.addNotification(this.translate.instant(isFromActivity ? 'Company.Activity.updateError' : 'Company.Identity.Notification.error'), 'error');
        console.error(err);
        return of(null);
      })
    );
  }

  retrieveContacts(): Observable<CompanyContact[]> {
    return this._http.get<CompanyContact[]>(this.__URL_API__ + 'api/Entreprise/ListeContacts').pipe(
      take(1),
      catchError((error) => {
        console.error(error);
        return of(null);
      })
    );
  }

  retrieveInvitations(): Observable<CompanyInvitation[]> {
    return this._http.get<CompanyInvitation[]>(this.__URL_API__ + 'api/Entreprise/ListeInvitations').pipe(
      take(1),
      catchError((error) => {
        console.error(error);
        return of(null);
      })
    );
  }

  getCompanyUsers(): Observable<CompanyUsers> {
    return this._http.get<CompanyUsers>(this.__URL_API__ + 'api/User').pipe(
      take(1),
      catchError((error) => {
        this.notificationService.addNotification(this.translate.instant('Users.load-error'), 'error');
        console.error(error);
        return of(null);
      })
    );
  }

  getSpecialUsers(): Observable<{ count: number; invitations: CompanyInvitation[] }> {
    return this._http.get<{ count: number; invitations: CompanyInvitation[] }>(this.__URL_API__ + 'api/User?isMain=true&isBilling=true').pipe(
      take(1),
      catchError((error) => {
        this.notificationService.addNotification(this.translate.instant('Users.load-error'), 'error');
        console.error(error);
        return of(null);
      })
    );
  }

  deleteCompanyUser(id: string, idContact: string, name: string): Observable<CompanyUsers> {
    const params = new HttpParams().set('idInvit', id).set('idContact', idContact);
    return this._http
      .patch<CompanyUsers>(this.__URL_API__ + 'api/User/delete', null, { params })
      .pipe(
        take(1),
        tap(() => this.notificationService.addNotification(this.translate.instant('Users.Delete.success', { name }), 'success', 15000)),
        tap(() => idContact),
        catchError((error) => {
          this.notificationService.addNotification(this.translate.instant('Users.load-error'), 'error');
          console.error(error);
          return of(null);
        })
      );
  }

  deleteInvit(id: string, name: string): Observable<any> {
    const url = `${this.__URL_API__}api/User/cancelInvit/${id}`;
    return this._http.patch(url, null).pipe(
      take(1),
      tap(() => this.notificationService.addNotification(this.translate.instant('Users.Delete.success', { name }), 'success', 15000)),
      tap(() => id),
      catchError((error) => {
        this.notificationService.addNotification(this.translate.instant('Users.load-error'), 'error');
        console.error(error);
        return of(null);
      })
    );
  }
}
